/**
 * Raised when the plot panel is asked to plot an integer outside JavaScript's safe
 * range (beyond ±(2^53 - 1)), where coercing it to a `number` would silently lose
 * precision. The usual trigger is a user plotting an int64 nanosecond timestamp as a
 * y value.
 *
 * The panel shows this error to the user and does not report it to Sentry, because
 * refusing the value is the intended outcome and not a fault in the app (ENG-2038,
 * ENG-3245). The `message` is written for the user: the panel renders it as the alert
 * body.
 */
export class PlotValueOutOfRangeError extends Error {
  /**
   * The discriminator is `name` rather than the class: the plot data streamer runs in
   * a web worker, and errors reach the main thread through `SerializableError`, which
   * rebuilds them as plain `Error`s carrying the original `name`. `instanceof` is
   * therefore false on the receiving side.
   */
  static readonly ERROR_NAME = "PlotValueOutOfRangeError";

  constructor(fieldPath: string, value: string | bigint) {
    super(
      `Value at "${fieldPath}" is too large in magnitude to plot (${value} exceeds the safe integer range). ` +
        "Consider using a smaller numeric type for this field.",
    );
    this.name = PlotValueOutOfRangeError.ERROR_NAME;
  }
}

/**
 * True for a `PlotValueOutOfRangeError`, including one rebuilt on the main thread from
 * a worker message, which is a plain `Error` and not an instance of the class.
 */
export function isPlotValueOutOfRangeError(error: unknown): boolean {
  return (
    error instanceof Error && error.name === PlotValueOutOfRangeError.ERROR_NAME
  );
}
